import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, tap, map, catchError } from 'rxjs';
import { AppConfigService } from 'src/app/app-config.service';

export type PlatformStatus =
  | 'not_initialized'
  | 'in_progress'
  | 'initialized'
  | 'failed'
  | 'degraded';

export interface BootstrapRequest {
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
}

export interface PlatformCheckItem {
  ok: boolean;
  detail?: string;
}

export interface PlatformChecks {
  healthy: boolean;
  realm_accessible?: PlatformCheckItem;
  realm_roles?: PlatformCheckItem & { missing: string[]; present: string[] };
  gui_client_roles?:
    | (PlatformCheckItem & {
        client_id: string;
        missing: string[];
        present: string[];
      })
    | null;
  admin_user_assigned?: PlatformCheckItem;
  management_client_mapping?: PlatformCheckItem;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformBootstrapService {
  status: PlatformStatus | null = null;
  checks: PlatformChecks | null = null;

  private bypassHttp: HttpClient;

  constructor(
    backend: HttpBackend,
    private http: HttpClient,
    private configService: AppConfigService,
  ) {
    // HttpClient créé avec HttpBackend : bypass tous les intercepteurs (pas de token requis)
    this.bypassHttp = new HttpClient(backend);
  }

  /**
   * Appelé dans APP_INITIALIZER. En cas d'erreur (service indisponible),
   * status reste null et l'app continue son flux normal.
   */
  loadStatus(): Observable<void> {
    const url = `${this.configService.getApiEndpoint('iamApi')}/platform/status`;
    return this.bypassHttp
      .get<{ status: PlatformStatus; checks?: PlatformChecks | null }>(url)
      .pipe(
        tap(response => {
          this.status = response.status;
          this.checks = response.checks ?? null;
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  /**
   * Appel sans intercepteur JWT (route exclue de auth_middleware, AD-007).
   * Authentification via Basic Auth avec les credentials du Service Account bootstrap.
   */
  bootstrap(
    request: BootstrapRequest,
    saClientId: string,
    saClientSecret: string,
  ): Observable<void> {
    const url = `${this.configService.getApiEndpoint('iamApi')}/platform/bootstrap`;
    const encoded = btoa(`${saClientId}:${saClientSecret}`);
    const headers = new HttpHeaders({ Authorization: `Basic ${encoded}` });
    return this.bypassHttp.post<void>(url, request, { headers });
  }

  repair(): Observable<void> {
    const url = `${this.configService.getApiEndpoint('iamApi')}/platform/repair`;
    return this.http.post<void>(url, {});
  }
}
